import { useEffect, useRef, useState } from 'react'
// Animation JSONs will be resolved dynamically from fileName prop
import './animationLoop.css'

/** Map desktop animation filenames to their mobile counterparts */
const MOBILE_VARIANTS: Record<string, string> = {
    'animation3.json': 'animation3-sm.json',
}

function useMobileFileName(fileName: string): string {
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    return isMobile && MOBILE_VARIANTS[fileName] ? MOBILE_VARIANTS[fileName] : fileName
}

export default function AnimationLoop({ fileName = 'animation1.json' }: { fileName?: string }) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const resolvedFileName = useMobileFileName(fileName)
    const [animUrl, setAnimUrl] = useState<string | null>(null)
    const [visible, setVisible] = useState(false)
    // Removed unused fontWeight state and resize effect

    // Resolve the selected animation file to a URL using Vite's glob
    useEffect(() => {
        const modules = import.meta.glob('./*.json', {
            query: '?url',
            import: 'default',
        }) as Record<string, () => Promise<string>>
        const key = `./${resolvedFileName}`

        if (modules[key]) {
            modules[key]().then((url) => setAnimUrl(url)).catch(() => setAnimUrl(null))
        } else {
            // fallback: pick the first available animation
            const first = Object.values(modules)[0]
            if (first) first().then((url) => setAnimUrl(url)).catch(() => setAnimUrl(null))
        }
    }, [resolvedFileName])

    // Only load lottie and initialize animation when element is visible and animUrl is ready
    useEffect(() => {
        if (!containerRef.current) return
        if (!animUrl) return
        if (!visible) return
        let cancelled = false
        let animInstance: any = null
        const el = containerRef.current
        // Fetch the JSON first to read custom metadata (links, pauseOnHover)
        const load = async () => {
            try {
                const resp = await fetch(animUrl)
                const json = await resp.json()
                const pauseOnHover = !!json?.pauseOnHover

                // dynamic import to keep lottie-web out of the main bundle until needed
                const lottieModule = await import('lottie-web')
                if (cancelled) return
                const lw: any = (lottieModule as any).default ?? lottieModule
                animInstance = lw.loadAnimation({
                    container: el as Element,
                    renderer: 'svg',
                    loop: true,
                    autoplay: true,
                    path: animUrl,
                    assetsPath: '/',
                    rendererSettings: {
                        preserveAspectRatio: 'xMidYMid slice',
                    },
                })
                if (animInstance && animInstance.setSpeed) animInstance.setSpeed(0.5)

                // pause on hover if requested (click navigation removed)
                if (el) {
                    const onEnter = () => { try { animInstance.pause && animInstance.pause() } catch (e) { } }
                    const onLeave = () => { try { animInstance.play && animInstance.play() } catch (e) { } }

                    if (pauseOnHover) {
                        el.addEventListener('mouseenter', onEnter)
                        el.addEventListener('mouseleave', onLeave)
                    }

                    // cleanup attachments when unloading
                    const cleanupListeners = () => {
                        if (pauseOnHover) {
                            el.removeEventListener('mouseenter', onEnter)
                            el.removeEventListener('mouseleave', onLeave)
                        }
                    }

                        // store cleanup on the instance so outer cleanup can call it
                        ; (animInstance as any).__cleanupListeners = cleanupListeners
                }
            } catch (err) {
                // ignore errors
            }
        }

        load()

        return () => {
            cancelled = true
            if (animInstance) {
                const cleanup = (animInstance as any).__cleanupListeners
                try { cleanup && cleanup() } catch (e) { }
                try { animInstance.destroy && animInstance.destroy() } catch (e) { }
            }
        }
    }, [animUrl, visible])

    // IntersectionObserver to mark visible when animation container enters viewport
    useEffect(() => {
        if (!containerRef.current) return
        const el = containerRef.current
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true)
                        io.disconnect()
                    }
                })
            },
            { threshold: 0.25 },
        )

        io.observe(el)

        return () => io.disconnect()
    }, [])

    return (
        <div className="anim-outer">
            <div className="anim-wrapper">
                <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            </div>
        </div>
    )
}
